'use client';

import { useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SessionContext } from '@benzinga/session-context';
import { GetQuotesLogoParams, Logo, QuotesManager, Security } from '@benzinga/quotes-manager';
import { safeUnwrapOrThrow } from '@benzinga/safe-await';

const FIVE_MINUTES = 5 * 60 * 1000;

export const LOGO_QUERY_KEY = 'quotesLogo' as const;

/**
 * Cache identity for a logo read. Symbols are sorted because asking for the same set in a
 * different order is the same question; the requested params are part of the key because
 * they decide which image variants come back.
 */
export const buildLogoQueryKey = (symbols: Security[], params?: GetQuotesLogoParams) => [
  LOGO_QUERY_KEY,
  { params: params ?? null, symbols: [...symbols].sort() },
];

export const useLogoQuery = (symbols: Security[], params?: GetQuotesLogoParams) => {
  const session = useContext(SessionContext);

  return useQuery<Logo[]>({
    enabled: symbols.some(Boolean),
    queryFn: async () => {
      const result = await session.getManager(QuotesManager).getQuotesLogos(symbols, params);

      return safeUnwrapOrThrow(result) ?? [];
    },
    queryKey: buildLogoQueryKey(symbols, params),
    staleTime: FIVE_MINUTES,
  });
};
